import React, {FC, useEffect, useMemo, useState} from "react";
import {Button, Col, Container, Form, FormControl, FormGroup, Row, Spinner} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    selectCalendarFirstDate,
    selectCalendarVisibleDays, selectDepartmentName, selectEmployeeCalendarData,
    setCalendarFirstDate,
    setCalendarVisibleDays
} from "../../../features/AppStateSlice";
import {dateToString} from "../../tools";
import {MaybeDate,} from "../../types/Maybe";
import RRTable from "../../components/rr-table/RRTable";
import CalendarHeader from "./CalendarHeader";
import CalendarBody from "./CalendarBody";
import EditCalendarEvent from "./EditCalendarEvent";
import LeftRightSelector from "../../components/LeftRightSelector";
import ReportCalendarEvent from "./ReportCalendarEvent";
import Frame, {FrameContextConsumer} from "react-frame-component";
import PrintEventDayProtocolFrame from "./PrintEventDayProtocolFrame";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHistory, faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";
import {RREventType} from "../../types/RREventType";
import {RRCalendarDateDataType} from "../../types/RRCalendarDataType";
import EventDetailsModal from "./EventDetailsModal";
import {calculateWorkhours} from "../../../firebase/FirebaseEmployeeStatisticsLoader";
import {selectCurrentEmployees} from "../../../features/AppDataSlice";

interface Props {

}

const CalendarView: FC<Props> = (props) => {
    const dispatch = useAppDispatch();
    const calendarFirstDate = useAppSelector(selectCalendarFirstDate);
    const visibleDays = useAppSelector(selectCalendarVisibleDays);
    const departmentName = useAppSelector(selectDepartmentName);
    const calendarData = useAppSelector(selectEmployeeCalendarData);

    const [datePickerValue, setDatePickerValue] = useState<MaybeDate>(calendarFirstDate);
    const [nameFilter, setNameFilter] = useState("");
    const [showEventDetails, setShowEventDetails] = useState<{
        event: RREventType,
        eventData: RRCalendarDateDataType
    } | undefined>();
    const employees = useAppSelector(selectCurrentEmployees);
    const [recalculateWorkhours, setRecalculateWorkhours] = useState(false);
    const frameHeadContent = useMemo(
        () => Array.from(document.head.querySelectorAll("link[rel='stylesheet'], style"))
            .map((node) => node.outerHTML)
            .join(""),
        []
    );

    useEffect(() => {
        setDatePickerValue(calendarFirstDate);
    }, [calendarFirstDate]);

    useEffect(() => {
        setNewStartDate()
    }, [datePickerValue]);

    function setNewStartDate() {
        dispatch(setCalendarFirstDate(datePickerValue || new Date()));
    }


    function subDay() {
        const newDate = new Date(calendarFirstDate);
        newDate.setDate(newDate.getDate() - 1);
        dispatch(setCalendarFirstDate(newDate));
    }


    function addDay() {
        const newDate = new Date(calendarFirstDate);
        newDate.setDate(newDate.getDate() + 1);
        dispatch(setCalendarFirstDate(newDate));
    }

    function subWeek() {
        const newDate = new Date(calendarFirstDate);
        newDate.setDate(newDate.getDate() - 7);
        dispatch(setCalendarFirstDate(newDate));
    }


    function addWeek() {
        const newDate = new Date(calendarFirstDate);
        newDate.setDate(newDate.getDate() + 7);
        dispatch(setCalendarFirstDate(newDate));
    }

    function subMonth() {
        const newDate = new Date(calendarFirstDate);
        newDate.setMonth(newDate.getMonth() - 1);
        dispatch(setCalendarFirstDate(newDate));
    }


    function addMonth() {
        const newDate = new Date(calendarFirstDate);
        newDate.setMonth(newDate.getMonth() + 1);
        dispatch(setCalendarFirstDate(newDate));
    }

    function subVisibleDays() {
        dispatch(setCalendarVisibleDays(visibleDays - 1))
    }

    function addVisibleDays() {
        dispatch(setCalendarVisibleDays(visibleDays + 1))
    }

    async function recalculateWorkedHours() {
        const emploeesIds = employees.map(e => e!.id!) || [];

        if (emploeesIds.length > 0) {
            setRecalculateWorkhours(true);
            await calculateWorkhours(calendarFirstDate, emploeesIds);
            setRecalculateWorkhours(false);
        }
    }

    return (
        <Container fluid>
            <h1>Календар {departmentName.toUpperCase()}</h1>

            <Row md={5} className={"g-2 mt-3 w-1200px flex-nowrap"}>
                <Col>
                    <Row>
                        <Col className={"pe-3 text-end"} xs={"auto"}>
                            <Form.Label>
                                Начална дата: </Form.Label>
                        </Col>
                        <Col>
                            <div className={"w-100px"}>
                                <ReactDatePicker
                                    selected={datePickerValue}
                                    value={dateToString(datePickerValue)}
                                    onChange={date => {
                                        setDatePickerValue(date);
                                    }}
                                    className={"form-control"}
                                />
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col className={"w-300px"}>
                    <Row className={"flex-nowrap w-160px"}>
                        <Col className={"pe-3 text-end"} xs={"auto"}>
                            <Form.Label className={"w-75px text-right"}>Брой дни: </Form.Label>
                        </Col>
                        <Col>
                            <LeftRightSelector onLeft={() => subVisibleDays()} onRight={() => addVisibleDays()}>
                                <FormGroup>
                                    <FormControl value={visibleDays.toString()} className={"text-center"}
                                                 type={"number"} readOnly/>
                                </FormGroup>
                            </LeftRightSelector>
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <LeftRightSelector onLeft={() => subDay()} onRight={() => addDay()}>
                        Ден
                    </LeftRightSelector>
                </Col>
                <Col>
                    <LeftRightSelector onLeft={() => subWeek()} onRight={() => addWeek()}>
                        Седмица
                    </LeftRightSelector>
                </Col>
                <Col xs={"auto"}>
                    <LeftRightSelector onLeft={() => subMonth()} onRight={() => addMonth()}>
                        Месец
                    </LeftRightSelector>
                </Col>
                <Col xs={"auto"}>
                    <Row>
                        <Col xs={"auto"} className={"pt-2"}>
                            <FontAwesomeIcon icon={faMagnifyingGlass} size={"xl"}/>
                        </Col>
                        <Col>
                            <FormControl className={"w-100px"}
                                         value={nameFilter}
                                         onChange={event => setNameFilter(event.target.value)}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={"auto"}>
                    {
                        recalculateWorkhours ? <Spinner animation={"border"} size={"sm"}/> :
                            <Button variant={"primary"} onClick={recalculateWorkedHours}
                                    title={"Актуализиране на отработените за месеца часове"}>
                                <FontAwesomeIcon icon={faHistory} size={"lg"}/>
                            </Button>
                    }
                </Col>
            </Row>

            <Row className={"mt-2"}>
                <Col>
                    <RRTable>
                        <CalendarHeader
                            onShowEventDetails={(event, eventData) => setShowEventDetails({event, eventData})}
                        />
                        <CalendarBody nameFilter={nameFilter}/>
                    </RRTable>
                </Col>
            </Row>

            <EditCalendarEvent/>
            <ReportCalendarEvent/>

            <Frame height={0} width={"100%"}
                   initialContent={`<!DOCTYPE html><html><head >
                        ${frameHeadContent}
                        <style>
                        @media print {
                          .table-bordered>:not(caption)>*>* {
                            border-width: 2px !important;
                            border-color: black;
                          }
                          }
                        </style>
                        </head><body><div class="frame-root"></div></body></html>`}>
                <FrameContextConsumer>
                    {
                        // Callback is invoked with iframe's window and document instances
                        ({document, window}: any) => {
                            return (<PrintEventDayProtocolFrame window={window}/>)
                        }
                    }
                </FrameContextConsumer>
            </Frame>

            {
                showEventDetails &&
                <EventDetailsModal
                    event={showEventDetails.event}
                    eventData={showEventDetails.eventData}
                    onClose={() => setShowEventDetails(undefined)}
                />
            }
        </Container>
    )
}

export default CalendarView;
